import { ReactElement, useCallback, useMemo } from 'react';
import { Collapse } from '@mui/material';
import Map, {
  NavigationControl,
  FullscreenControl,
  Marker,
} from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';

import MarkerWarehouse from '~components/MarkerWarehouse';
import {
  ModeSateliteControl,
  ShowTripViewControl,
  LayersControl,
  SelectionOrderControl,
  SelectionControl,
} from '~components/MapCustomControl';
import { LayersSelector } from '~components/MapCustomControl/LayersControl';
import { SelectionControlFeatures } from '~components/MapCustomControl/SelectionControl/types';
import { Marker as MarkerIcon } from '~components/Icons';
import { useToggle } from '~hooks/index';
import {
  usePopupRequest,
  PopupRequestOpenPopupHandler,
} from '~hooks/usePopupRequest';
import { getItemInfo } from '~services/item';
import { getWarehouseInfo } from '~services/warehouse';
import { TOKEN } from '~utils/mapbox';
import { MapControlBaseEventHandler } from '~utils/mapClasses/ControlBar';
import { controlLocale } from '~constants/mapControlLocale';

import { useDashboardVariantV2CarrierContentCollectTabContext } from '../../Context';
import {
  DashboardVariantV2CarrierContentMapLoading,
  DashboardVariantV2CarrierContentMapContainer,
} from '../../../../styles';

// eslint-disable-next-line max-len
import DashboardVariantV2CarrierContentCollectTabMapPopup from '../../../InWarehouse/Components/Map/Popup';

const DashboardVariantV2CarrierContentCollectTabMap = (): ReactElement => {
  const {
    selection: { utils: selectedIdsUtils },
    map: {
      showMap: { open: showMap },
      data: {
        orders: { data: orders, loading: loadingOrders },
        warehouses: { data: warehouses, loading: loadingWarehouses },
      },
    },
    dialogs: {
      detailsOrder: { handleOpen: handleOpenDialogDetailsOrder },
    },
  } = useDashboardVariantV2CarrierContentCollectTabContext();

  const bounds = useMemo(() => {
    const boundsInstance = new mapboxgl.LngLatBounds();

    orders.forEach((order) => {
      if (order.coordinates.length > 1) {
        boundsInstance.extend(order.coordinates);
      }
    });

    warehouses.forEach((warehouse) => {
      if (warehouse.coordinates.length > 1) {
        boundsInstance.extend(warehouse.coordinates);
      }
    });

    return boundsInstance;
  }, [orders, warehouses]);

  const { handleOpenPopup: handleOpenPopupOrder, ...restPopupOrder } =
    usePopupRequest({
      request: getItemInfo,
    });

  const { handleOpenPopup: handleOpenPopupWarehouse, ...restPopupWarehouse } =
    usePopupRequest({
      request: getWarehouseInfo,
    });

  const [enabledSelectedMarkers, toggleEnabledSelectedMarkers] =
    useToggle(false);

  const [isDrawing, toggleIsDrawing] = useToggle(false);

  const selectedCapacities = useMemo(
    () =>
      orders
        .filter((order) => selectedIdsUtils.has(order.itemId))
        .map((order) => ({
          capacities: { primary: order.units[0], secondary: order.units[1] },
        })),
    [orders, selectedIdsUtils],
  );

  const markerCursor = useMemo(
    () => (enabledSelectedMarkers ? 'pointer' : 'grab'),
    [enabledSelectedMarkers],
  );

  const getCurrentColorMarker = useCallback(
    (markerId: string) => (selectedIdsUtils.has(markerId) ? '#5618b6' : '#000'),
    [selectedIdsUtils],
  );

  const handleCreateDraw = useCallback<
    MapControlBaseEventHandler<SelectionControlFeatures>
  >(
    (event) => {
      const selectedPolygon = event.features[0];

      const drawSelectedIds: string[] = [];

      for (const order of orders) {
        if (order.coordinates.length > 1) {
          const isSelectedOnPolygon = turf.booleanPointInPolygon(
            order.coordinates,
            selectedPolygon,
          );

          if (isSelectedOnPolygon) {
            drawSelectedIds.push(order.itemId);
          }
        }
      }

      selectedIdsUtils.addMultiple(drawSelectedIds);

      setTimeout(() => toggleIsDrawing(false), 0);
    },
    [orders, selectedIdsUtils, toggleIsDrawing],
  );

  const handleClickOrderMarker = useCallback<PopupRequestOpenPopupHandler>(
    async (event, id, coordinates) => {
      if (isDrawing) return;

      if (enabledSelectedMarkers) {
        selectedIdsUtils.toggle(id);
      } else {
        await handleOpenPopupOrder(event, id, coordinates);
      }
    },
    [enabledSelectedMarkers, handleOpenPopupOrder, isDrawing, selectedIdsUtils],
  );

  const handleClickOrderWarehouse = useCallback<PopupRequestOpenPopupHandler>(
    async (event, id, coordinates) => {
      if (isDrawing) return;

      await handleOpenPopupWarehouse(event, id, coordinates);
    },
    [handleOpenPopupWarehouse, isDrawing],
  );

  const handleSelectAllOnMapSelection = useCallback(() => {
    const ordersIds = orders.map((order) => order.itemId);

    selectedIdsUtils.addMultiple(ordersIds);
  }, [orders, selectedIdsUtils]);

  const generaloading = useMemo(
    () => loadingOrders || loadingWarehouses,
    [loadingOrders, loadingWarehouses],
  );

  return (
    <Collapse orientation="horizontal" in={showMap}>
      {generaloading && (
        <DashboardVariantV2CarrierContentMapLoading type="relative" />
      )}

      {!generaloading && (
        <DashboardVariantV2CarrierContentMapContainer fullWidth={false}>
          <LayersControl
            hiddenMarkers
            hiddenTrips
            hiddenVehicles
            hiddenRouteItemState
            hiddenOrders={false}
            hiddenWarehouses={false}
          >
            {({
              enabledOrders,
              enabledWarehouses,
              checkedLayers,
              layersList,
              toggleLayer,
            }) => (
              <Map
                mapboxAccessToken={TOKEN}
                mapStyle="mapbox://styles/mapbox/light-v10"
                initialViewState={{
                  bounds,
                  fitBoundsOptions: {
                    padding: 100,
                    maxZoom: 12,
                  },
                }}
                attributionControl={false}
                locale={controlLocale}
              >
                <FullscreenControl position="bottom-left" />
                <NavigationControl position="bottom-left" showCompass={false} />
                <ShowTripViewControl
                  position="bottom-left"
                  currentBounds={bounds}
                />
                <ModeSateliteControl position="bottom-left" />

                <SelectionControl
                  position="top-right"
                  actions={{
                    onInitalizeDraw: () => toggleIsDrawing(true),
                    onDraw: handleCreateDraw,
                    onFreHand: () => toggleEnabledSelectedMarkers(false),
                    onSelectedMarkers: () => toggleEnabledSelectedMarkers(),
                  }}
                />

                <SelectionOrderControl
                  orders={selectedCapacities}
                  totalOrdersCount={orders.length}
                  onSelectAll={handleSelectAllOnMapSelection}
                  onClear={() => selectedIdsUtils.reset()}
                />

                <LayersSelector
                  checkedLayers={checkedLayers}
                  layersList={layersList}
                  toggleLayer={toggleLayer}
                />

                {enabledOrders &&
                  orders.map(
                    (orderMarker) =>
                      orderMarker.coordinates.length > 1 && (
                        <Marker
                          key={`marker-order-${orderMarker.itemId}`}
                          longitude={orderMarker.coordinates[0]}
                          latitude={orderMarker.coordinates[1]}
                          onClick={(e) =>
                            handleClickOrderMarker(
                              e,
                              orderMarker.itemId,
                              orderMarker.coordinates,
                            )
                          }
                          anchor="bottom"
                          style={{ cursor: markerCursor }}
                        >
                          <MarkerIcon
                            htmlColor={getCurrentColorMarker(
                              orderMarker.itemId,
                            )}
                          />
                        </Marker>
                      ),
                  )}

                <DashboardVariantV2CarrierContentCollectTabMapPopup
                  {...restPopupOrder}
                  isItem
                  onShowDetailOrder={handleOpenDialogDetailsOrder}
                />

                {enabledWarehouses &&
                  warehouses.map(
                    (warehouseMarker) =>
                      warehouseMarker.coordinates.length > 1 && (
                        <Marker
                          key={`marker-warehouse-${warehouseMarker.warehouseId}`}
                          longitude={warehouseMarker.coordinates[0]}
                          latitude={warehouseMarker.coordinates[1]}
                          onClick={(e) =>
                            handleClickOrderWarehouse(
                              e,
                              warehouseMarker.warehouseId,
                              warehouseMarker.coordinates,
                            )
                          }
                          anchor="bottom"
                        >
                          <MarkerWarehouse
                            color="#00a200"
                            includeBackgroundColor
                          />
                        </Marker>
                      ),
                  )}

                <DashboardVariantV2CarrierContentCollectTabMapPopup
                  {...restPopupWarehouse}
                />
              </Map>
            )}
          </LayersControl>
        </DashboardVariantV2CarrierContentMapContainer>
      )}
    </Collapse>
  );
};

export default DashboardVariantV2CarrierContentCollectTabMap;
